import { Link } from "react-router-dom";
import { useTheme } from "@/App";
import { cn } from "@/lib/utils";
import { ArrowLeft } from "lucide-react";

const sections = [
  {
    title: "Who We Are",
    content: (
      <>
        <p>FirstHold ("we", "us", "our") is a freelance business management platform operated by Paolo Cogliati LLC.</p>
        <p>Contact: <a href="mailto:hello@firsthold.io" className="underline underline-offset-2">hello@firsthold.io</a></p>
      </>
    ),
  },
  {
    title: "What We Collect",
    content: (
      <ul>
        <li><strong>Account information</strong> — name, email address, password (hashed — never stored in plaintext)</li>
        <li><strong>Professional data you enter</strong> — booking records, client names, earnings figures, rates, invoice details, availability ranges</li>
        <li><strong>Usage data</strong> — pages visited, features used, session duration — collected via Sentry session replay (10% of sessions) for product improvement</li>
        <li><strong>Payment information</strong> — handled entirely by Stripe. We never store card numbers or payment credentials</li>
        <li><strong>Tax estimation inputs</strong> — country, filing status, deduction amounts — stored to persist your settings across sessions</li>
      </ul>
    ),
  },
  {
    title: "How We Use Your Data",
    content: (
      <ul>
        <li>To provide and improve the FirstHold service</li>
        <li>To calculate and display your earnings, statistics, and tax estimates</li>
        <li>To generate invoices on your behalf using information you provide</li>
        <li>To analyze aggregated, anonymized usage patterns and industry trends across our user base — this data cannot identify individual users</li>
        <li>To send transactional emails (account confirmation, subscription receipts) — we do not send marketing emails without explicit opt-in</li>
        <li>To detect and prevent fraud, abuse, and security incidents</li>
      </ul>
    ),
  },
  {
    title: "Aggregated and Anonymized Data",
    content: (
      <p>We may derive anonymized, aggregated statistics from user data — such as average freelance day rates by discipline or market trends — with all personally identifiable information permanently removed. This data may be used internally for product development, published as industry benchmarks, or shared with third parties in aggregate form only. No individual user can be identified from this data.</p>
    ),
  },
  {
    title: "Who We Share Your Data With",
    content: (
      <>
        <ul>
          <li><strong>Supabase</strong> — database and file storage (US-based; EU data residency available)</li>
          <li><strong>Stripe</strong> — payment processing, subject to Stripe's own privacy policy</li>
          <li><strong>PDFMonkey</strong> — PDF generation; invoice data is sent to generate PDFs and is not stored by PDFMonkey beyond generation</li>
          <li><strong>Google Gemini</strong> — AI parsing of uploaded files; file content is sent to Google's API for extraction and is not used to train Google models per Google's API terms</li>
          <li><strong>Sentry</strong> — error monitoring and session replay; anonymized session data only</li>
          <li><strong>Vercel</strong> — hosting and serverless functions; request data only</li>
        </ul>
        <p>We do not sell your personal data to third parties.</p>
      </>
    ),
  },
  {
    title: "Data Retention",
    content: (
      <ul>
        <li><strong>Active account data</strong> — retained for the lifetime of your account</li>
        <li><strong>Deleted account data</strong> — EU users: erased immediately via in-app deletion in Settings. All other users: email <a href="mailto:hello@firsthold.io" className="underline underline-offset-2">hello@firsthold.io</a> to request deletion; we process these requests within 30 days</li>
        <li><strong>Aggregated anonymized data</strong> — retained indefinitely (contains no personal information)</li>
        <li><strong>Stripe payment records</strong> — retained per Stripe's policy and applicable financial regulations</li>
      </ul>
    ),
  },
  {
    title: "Your Rights",
    content: (
      <>
        <p className="font-semibold">All users:</p>
        <ul>
          <li>Active subscribers can export bookings, invoices, earnings, and availability data via Settings → Export Your Data. Anyone can request a full copy of their data by emailing <a href="mailto:hello@firsthold.io" className="underline underline-offset-2">hello@firsthold.io</a>, free of charge</li>
          <li>EU users can delete their account and all associated data immediately via Settings. All other users: email <a href="mailto:hello@firsthold.io" className="underline underline-offset-2">hello@firsthold.io</a> to request deletion, processed within 30 days</li>
          <li>Correct inaccurate data by editing it directly in the app</li>
        </ul>
        <p className="font-semibold mt-4">EU/EEA users (GDPR):</p>
        <ul>
          <li>Right to data portability — available in-app for active subscribers (Settings → Export Your Data); free of charge for all EU users on request, regardless of plan</li>
          <li>Right to erasure ("right to be forgotten") — available in-app for EU users</li>
          <li>Right to restrict processing — contact <a href="mailto:hello@firsthold.io" className="underline underline-offset-2">hello@firsthold.io</a></li>
          <li>Right to object to processing — contact <a href="mailto:hello@firsthold.io" className="underline underline-offset-2">hello@firsthold.io</a></li>
          <li>Right to lodge a complaint with your local supervisory authority</li>
        </ul>
      </>
    ),
  },
  {
    title: "Cookies and Local Storage",
    content: (
      <p>We use browser localStorage (not cookies) to store your session token and preferences. We do not use advertising cookies or third-party tracking pixels.</p>
    ),
  },
  {
    title: "Security",
    content: (
      <p>Data is encrypted in transit (TLS) and at rest (AES-256 via Supabase). Passwords are hashed and never stored in plaintext. We conduct regular security reviews and access is restricted to authorized personnel only.</p>
    ),
  },
  {
    title: "Children",
    content: (
      <p>FirstHold is intended for professional use by adults. We do not knowingly collect data from anyone under 16.</p>
    ),
  },
  {
    title: "Changes to This Policy",
    content: (
      <p>We will notify users of material changes via email or in-app notice at least 14 days before changes take effect.</p>
    ),
  },
  {
    title: "Contact",
    content: (
      <p><a href="mailto:hello@firsthold.io" className="underline underline-offset-2">hello@firsthold.io</a></p>
    ),
  },
];

export default function PrivacyPage() {
  const { isDark } = useTheme();

  const pageBg   = isDark ? "bg-gradient-to-b from-[#0f1419] via-[#131820] to-[#0a0e12] text-white" : "bg-[#E3E8F1] text-[#1A1F36]";
  const cardBg   = isDark ? "bg-white/5 border-white/10" : "bg-white/80 border-white";
  const mutedTxt = isDark ? "text-white/50" : "text-[#1A1F36]/50";
  const bodyTxt  = isDark ? "text-white/80" : "text-[#1A1F36]/80";
  const hrCls    = isDark ? "border-white/10" : "border-black/5";
  const hoverBg  = isDark ? "hover:text-white/70" : "hover:text-[#1A1F36]/70";

  return (
    <div className={cn("min-h-screen font-sans", pageBg)}>
      <main className="max-w-3xl mx-auto px-6 py-12 pb-24">

        {/* Back */}
        <Link
          to="/login"
          className={cn("inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] mb-12 transition-opacity hover:opacity-60", mutedTxt)}
        >
          <ArrowLeft size={12} />
          FirstHold
        </Link>

        {/* Header */}
        <div className="mb-10">
          <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-3">Privacy Policy</h1>
          <p className={cn("text-sm font-medium", mutedTxt)}>Last updated July 1, 2026</p>
        </div>

        {/* Sections */}
        <div className={cn("rounded-[2rem] border divide-y", cardBg, isDark ? "divide-white/10" : "divide-black/5")}>
          {sections.map((s, i) => (
            <section key={i} className="px-8 py-8">
              <h2 className="text-[10px] font-black uppercase tracking-[0.2em] mb-4 opacity-40">{i + 1}. {s.title}</h2>
              <div className={cn(
                "text-sm leading-relaxed space-y-3",
                bodyTxt,
                "[&_ul]:space-y-2 [&_ul]:list-disc [&_ul]:pl-5",
                "[&_li]:leading-relaxed",
                "[&_strong]:font-bold [&_strong]:opacity-100",
                "[&_a]:underline [&_a]:underline-offset-2",
                `[&_a]:${hoverBg}`
              )}>
                {s.content}
              </div>
            </section>
          ))}
        </div>

        {/* Footer */}
        <div className="text-center pt-12">
          <p className={cn("text-[10px] font-bold uppercase tracking-[0.2em]", mutedTxt)}>
            © 2026 FirstHold &nbsp;·&nbsp;
            <Link to="/terms" className="hover:opacity-60 transition-opacity">Terms of Service</Link>
          </p>
        </div>

      </main>
    </div>
  );
}
